import * as THREE from "three";
import { DbLine } from "./DbLine";
import { Context } from "./context";
import { encodeLineType } from "@/vendor/protobuf/packet2d";
import { CommonColor } from "./Parameters";

export enum EditAssistModelType {
  START = 0,
  END = 1,
  MID = 2,
}

export class EditAssistModel {
  private m_editAssistBaseMaterial = new THREE.MeshBasicMaterial({
    color: CommonColor.EditAssistBase,
  });
  private m_editAssistActiveMaterial = new THREE.MeshBasicMaterial({
    color: CommonColor.EditAssistActive,
  });
  public get EditAssistBaseMaterial() {
    return this.m_editAssistBaseMaterial;
  }
  public get EditAssistActiveMaterial() {
    return this.m_editAssistActiveMaterial;
  }
  private m_editAssistModelGroup: THREE.Group | undefined;
  private m_editAssistModelScale = 1;
  public get EditAssistModelGroup() {
    return this.m_editAssistModelGroup;
  }
  private m_context: Context;

  constructor(context: Context) {
    this.m_context = context;
  }
  private get scale() {
    return this.m_context.ViewManager.ScreenModelScale;
  }
  private m_baseLength = 0;

  Create(dbLine: DbLine) {
    if (!this.m_editAssistModelGroup) {
      this.m_editAssistModelGroup = new THREE.Group();
      this.m_context.SceneManager.AuxModel.add(this.m_editAssistModelGroup);
    }
    let startPoint = new THREE.Vector3(
      dbLine.points[0],
      dbLine.points[1],
      dbLine.points[2]
    );
    let endPoint = new THREE.Vector3(
      dbLine.points.at(-3),
      dbLine.points.at(-2),
      dbLine.points.at(-1)
    );
    let midPoint = new THREE.Vector3().lerpVectors(startPoint, endPoint, 0.5);
    let scale = this.scale * 10;

    let originGeo = new THREE.BoxGeometry(
      this.m_editAssistModelScale,
      this.m_editAssistModelScale,
      this.m_editAssistModelScale
    );
    let originPositions = originGeo.getAttribute("position").array;
    let originPoints = [];
    for (let i = 0; i < originPositions.length; i += 3) {
      let point = new THREE.Vector3(
        originPositions[i],
        originPositions[i + 1],
        originPositions[i + 2]
      );
      originPoints.push(point);
    }
    this.m_baseLength = new THREE.Vector3()
      .subVectors(originPoints[0], new THREE.Vector3())
      .length();

    let startEditAssistGeo = originGeo.clone();
    let startOriginPoints = originPoints.map((originPoint) => {
      return new THREE.Vector3().addVectors(originPoint, startPoint);
    });
    startEditAssistGeo.setFromPoints(startOriginPoints);
    startEditAssistGeo.computeBoundingBox();
    startEditAssistGeo.computeBoundingSphere();
    let startEditAssistMesh = new THREE.Mesh(
      startEditAssistGeo,
      this.m_editAssistBaseMaterial.clone()
    );
    this.UpdatedModelByScale(startEditAssistMesh, scale);

    let endEditAssistGeo = originGeo.clone();
    let endOriginPoints = originPoints.map((originPoint) => {
      return new THREE.Vector3().addVectors(originPoint, endPoint);
    });
    endEditAssistGeo.setFromPoints(endOriginPoints);
    endEditAssistGeo.computeBoundingBox();
    endEditAssistGeo.computeBoundingSphere();
    let endEditAssistMesh = new THREE.Mesh(
      endEditAssistGeo,
      this.m_editAssistBaseMaterial.clone()
    );
    this.UpdatedModelByScale(endEditAssistMesh, scale);

    let midEditAssistGeo = originGeo.clone();
    let midOriginPoints = originPoints.map((originPoint) => {
      return new THREE.Vector3().addVectors(originPoint, midPoint);
    });
    midEditAssistGeo.setFromPoints(midOriginPoints);
    midEditAssistGeo.computeBoundingBox();
    midEditAssistGeo.computeBoundingSphere();
    let midEditAssistMesh = new THREE.Mesh(
      midEditAssistGeo,
      this.m_editAssistBaseMaterial.clone()
    );
    this.UpdatedModelByScale(midEditAssistMesh, scale);
    startEditAssistMesh.userData = {
      type: EditAssistModelType.START,
      originData: dbLine,
    };
    endEditAssistMesh.userData = {
      type: EditAssistModelType.END,
      originData: dbLine,
    };
    midEditAssistMesh.userData = {
      type: EditAssistModelType.MID,
      originData: dbLine,
    };

    this.m_editAssistModelGroup.add(
      startEditAssistMesh,
      endEditAssistMesh,
      midEditAssistMesh
    );
  }

  UpdatedModelByScale(model: THREE.Mesh, scale: number) {
    let originPositions = model.geometry.getAttribute("position").array;
    let originPoints = [];
    for (let i = 0; i < originPositions.length; i += 3) {
      let point = new THREE.Vector3(
        originPositions[i],
        originPositions[i + 1],
        originPositions[i + 2]
      );
      originPoints.push(point);
    }
    let newPoints = [];
    let center = model.geometry.boundingSphere!.center;
    for (let i = 0; i < originPoints.length; i++) {
      let vector = new THREE.Vector3().subVectors(originPoints[i], center);
      let newLength = this.m_baseLength * scale;
      let newPoint = vector.normalize().setLength(newLength).add(center);
      newPoints.push(newPoint);
    }
    model.geometry.setFromPoints(newPoints);
    model.geometry.computeBoundingBox();
    model.geometry.computeBoundingSphere();
  }

  UpdatedModelMaterialByPoint(model: THREE.Mesh, point: THREE.Vector3) {
    let center = model.geometry.boundingSphere!.center;
    if (point.distanceTo(center) <= 0.001) {
      model.material = this.m_editAssistActiveMaterial.clone();
      return true;
    }
    return false;
  }

  Dispose() {
    if (this.m_editAssistModelGroup) {
      for (let i = 0; i < this.m_editAssistModelGroup.children.length; i++) {
        const model = this.m_editAssistModelGroup.children[i] as any;
        model.geometry.dispose();
        model.material.dispose();
        model.removeFromParent();
        i--;
      }
      this.m_context.SceneManager.AuxModel.remove(this.m_editAssistModelGroup);
      this.m_editAssistModelGroup = undefined;
    }
  }
}
